import { useMemo, useState } from 'react';
import type { ColumnDef } from '@deckspace/types';

interface CalendarViewProps {
  nodeId: string;
  mapping: { date: string; title: string; color?: string; endDate?: string };
  view?: 'month' | 'week';
  // Injected by withDataset HOC (Task 8):
  rows: Record<string, unknown>[];
  columnDefs?: ColumnDef[];
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function CalendarView({ mapping, view: _view = 'month', rows }: CalendarViewProps) {
  const [currentDate] = useState(() => new Date());

  const { days, events } = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const daysList: Array<{ date: number; inMonth: boolean }> = [];
    for (let i = 0; i < firstDay; i++) daysList.push({ date: 0, inMonth: false });
    for (let d = 1; d <= daysInMonth; d++) daysList.push({ date: d, inMonth: true });
    while (daysList.length % 7 !== 0) daysList.push({ date: 0, inMonth: false });

    const eventsMap = new Map<number, Array<{ title: string; color: string }>>();
    if (Array.isArray(rows)) {
      for (const row of rows) {
        const dateStr = String(row[mapping.date] ?? '');
        const parsed = new Date(dateStr);
        if (parsed.getFullYear() === year && parsed.getMonth() === month) {
          const day = parsed.getDate();
          if (!eventsMap.has(day)) eventsMap.set(day, []);
          eventsMap.get(day)!.push({
            title: String(row[mapping.title] ?? ''),
            color: mapping.color ? String(row[mapping.color] ?? '#3b82f6') : '#3b82f6',
          });
        }
      }
    }

    return { days: daysList, events: eventsMap };
  }, [currentDate, rows, mapping.date, mapping.title, mapping.color]);

  return (
    <div className="flex flex-col gap-1">
      <div className="grid grid-cols-7 gap-px text-center text-xs font-medium text-zinc-500">
        {DAYS.map((d) => (
          <div key={d} className="py-1">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px">
        {days.map((day, i) => (
          <div
            key={i}
            className={`min-h-[3rem] border border-zinc-100 p-1 text-xs dark:border-zinc-800 ${
              day.inMonth ? '' : 'bg-zinc-50 dark:bg-zinc-900'
            }`}
          >
            {day.inMonth && (
              <>
                <div className="font-medium text-zinc-600 dark:text-zinc-400">{day.date}</div>
                {events.get(day.date)?.map((evt, j) => (
                  <div
                    key={j}
                    className="mt-0.5 truncate rounded px-1 text-[10px]"
                    style={{
                      backgroundColor: evt.color + '20',
                      color: evt.color,
                    }}
                  >
                    {evt.title}
                  </div>
                ))}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
